// Teilnehmer-RSVP für Trainingsstunden (Eltern/Spieler self-service).

function __asStr(x) {
  return String(x ?? "").trim();
}

/** @param {unknown} raw */
export function normalizeSessionPersonIdList(raw) {
  const src = Array.isArray(raw) ? raw : [];
  return [
    ...new Set(
      src
        .map((x) => __asStr(typeof x === "object" && x?.id != null ? x.id : x))
        .filter(Boolean),
    ),
  ];
}

/** @param {object|null|undefined} session */
export function normalizePlannedIds(session) {
  return normalizeSessionPersonIdList(session?.planned_ids);
}

/**
 * @param {object|null|undefined} session
 * @param {string} personId
 */
export function isPersonDeclinedForSession(session, personId) {
  const pid = __asStr(personId);
  if (!pid) return false;
  return normalizeSessionPersonIdList(session?.declined_ids).includes(pid);
}

/**
 * @param {object|null|undefined} session
 * @param {string} personId
 * @returns {'not_planned'|'attending'|'declined'}
 */
export function resolveSessionSelfRsvp(session, personId) {
  const pid = __asStr(personId);
  const planned = normalizeSessionPersonIdList(session?.planned_ids);
  if (!pid || !planned.includes(pid)) return "not_planned";
  return isPersonDeclinedForSession(session, personId)
    ? "declined"
    : "attending";
}

/**
 * Geplanter künftiger Termin und Person hat nicht abgesagt.
 * @param {object|null|undefined} session
 * @param {string} personId
 */
export function isPersonAttendingUpcomingSession(session, personId) {
  return resolveSessionSelfRsvp(session, personId) === "attending";
}

/**
 * Alle geplanten Teilnehmer haben abgesagt (Session droht auszufallen).
 * @param {object|null|undefined} session
 */
/**
 * Geplante Teilnehmer, die nicht selbst abgesagt haben.
 * @param {object|null|undefined} session
 */
export function getSessionAttendingPersonIds(session) {
  if (session?.attendance_confirmed) {
    return normalizeSessionPersonIdList(session?.present_ids);
  }
  const planned = normalizeSessionPersonIdList(session?.planned_ids);
  const declined = new Set(normalizeSessionPersonIdList(session?.declined_ids));
  return planned.filter((id) => !declined.has(id));
}

/**
 * Startwerte für Anwesenheits-Bestätigung: gespeicherte present_ids, sonst geplante Teilnehmer.
 * @param {object|null|undefined} session
 */
export function resolveAttendanceConfirmPresentIds(session) {
  const planned = getSessionAttendingPersonIds(session);
  const saved = normalizeSessionPersonIdList(session?.present_ids);
  if (!saved.length) return planned;
  const plannedSet = new Set(planned);
  const filtered = saved.filter((id) => plannedSet.has(id));
  return filtered.length ? filtered : planned;
}

/**
 * Geplante Teilnehmer mit Self-Decline (Eltern/Spieler-Portal).
 * @param {object|null|undefined} session
 */
export function getSessionDeclinedPersonIds(session) {
  const planned = new Set(normalizeSessionPersonIdList(session?.planned_ids));
  return normalizeSessionPersonIdList(session?.declined_ids).filter((id) =>
    planned.has(id),
  );
}

/**
 * Entfernt bestätigte Anwesenheit aus declined_ids (Trainer-Override bei Absage).
 * @param {object|null|undefined} session
 * @param {unknown} presentIds
 */
export function resolveDeclinedIdsAfterAttendanceConfirm(session, presentIds) {
  const present = new Set(normalizeSessionPersonIdList(presentIds));
  const planned = normalizeSessionPersonIdList(session?.planned_ids);
  const keptDeclined = normalizeSessionPersonIdList(
    session?.declined_ids,
  ).filter((id) => !present.has(id));
  const absentFromPlanned = planned.filter((id) => !present.has(id));
  return [...new Set([...keptDeclined, ...absentFromPlanned])];
}

/**
 * Geplante Teilnehmer, die zum Bestätigungszeitpunkt abgesagt hatten, der Trainer sie aber dennoch als anwesend markiert.
 * @param {object|null|undefined} session
 * @param {unknown} presentIds
 */
export function resolvePresentDespiteDeclineIds(session, presentIds) {
  const present = new Set(normalizeSessionPersonIdList(presentIds));
  const declined = new Set(normalizeSessionPersonIdList(session?.declined_ids));
  return [...present].filter((id) => declined.has(id));
}

/**
 * @param {object|null|undefined} session
 * @param {string} personId
 */
export function isPersonPresentDespiteDecline(session, personId) {
  const pid = __asStr(personId);
  if (!pid) return false;
  return normalizeSessionPersonIdList(
    session?.present_despite_decline_ids,
  ).includes(pid);
}

/**
 * @param {object|null|undefined} session
 * @param {string} personId
 * @returns {'not_planned'|'planned_open'|'participated'|'declined'|'absent'}
 */
export function resolvePlannedParticipantAttendanceStatus(session, personId) {
  const pid = __asStr(personId);
  const planned = normalizePlannedIds(session);
  if (!pid || !planned.includes(pid)) return "not_planned";

  const confirmed = Boolean(session?.attendance_confirmed);
  const present = normalizeSessionPersonIdList(session?.present_ids).includes(
    pid,
  );
  const declined = isPersonDeclinedForSession(session, personId);

  if (confirmed) {
    if (present) return "participated";
    if (declined) return "declined";
    return "absent";
  }
  if (declined) return "declined";
  return "planned_open";
}

/**
 * i18n-Key für Teilnehmer-Badge in der Agenda (Spieler-Suche aktiv).
 * @param {'participated'|'declined'|'absent'|'planned_open'} status
 */
export function participantAttendanceStatusLabelKey(status) {
  const s = __asStr(status);
  if (s === "participated")
    return "trainerstunden.agenda.participantStatus.participated";
  if (s === "declined")
    return "trainerstunden.agenda.participantStatus.declined";
  if (s === "absent") return "trainerstunden.agenda.participantStatus.absent";
  return "trainerstunden.agenda.participantStatus.plannedOpen";
}

/**
 * @param {object|null|undefined} session
 * @param {string[]} personIds
 * @returns {'not_planned'|'planned_open'|'participated'|'declined'|'absent'}
 */
export function resolvePlannedParticipantAttendanceStatusForAny(
  session,
  personIds,
) {
  const ids = normalizeSessionPersonIdList(personIds);
  if (!ids.length) return "not_planned";
  const statuses = ids.map((id) =>
    resolvePlannedParticipantAttendanceStatus(session, id),
  );
  if (statuses.includes("participated")) return "participated";
  if (statuses.includes("absent")) return "absent";
  if (statuses.includes("declined")) return "declined";
  if (statuses.includes("planned_open")) return "planned_open";
  return "not_planned";
}

export function isSessionAllPlannedParticipantsDeclined(session) {
  const st = session && typeof session === "object" ? session : {};
  if (st.attendance_confirmed) return false;
  const planned = normalizeSessionPersonIdList(st.planned_ids);
  if (!planned.length) return false;
  const declined = new Set(normalizeSessionPersonIdList(st.declined_ids));
  return planned.every((id) => declined.has(id));
}

/**
 * @param {object[]|null|undefined} sessions
 * @param {{ now?: Date }} [opts]
 */
export function listSessionsAllParticipantsDeclined(sessions, opts = {}) {
  const now = opts.now instanceof Date ? opts.now : new Date();
  const todayIso = now.toISOString().slice(0, 10);
  const nowMs = now.getTime();
  return (Array.isArray(sessions) ? sessions : []).filter((s) => {
    if (!isSessionAllPlannedParticipantsDeclined(s)) return false;
    const status = __asStr(s?.status).toLowerCase();
    if (status.startsWith("cancelled") || status === "postponed") return false;
    const date = __asStr(s?.date).slice(0, 10);
    if (!date) return false;
    if (date > todayIso) return true;
    if (date < todayIso) return false;
    const startRaw = __asStr(s?.start_time);
    if (!startRaw) return true;
    const startMs = Date.parse(`${date}T${startRaw}`);
    return Number.isFinite(startMs) && startMs > nowMs;
  });
}

/**
 * Patch für Trainer-Umschalten Anwesend/Abwesend eines geplanten Teilnehmers.
 * Unbestätigt: declined_ids. Bestätigt: present_ids (+ abgeleitete declined_ids).
 * @param {object|null|undefined} session
 * @param {string} personId
 * @returns {Record<string, unknown>|null}
 */
export function buildSessionParticipantAttendanceTogglePatch(
  session,
  personId,
) {
  const pid = __asStr(personId);
  if (!session || !pid) return null;
  const planned = normalizePlannedIds(session);
  if (!planned.includes(pid)) return null;

  if (session.attendance_confirmed) {
    const present = new Set(normalizeSessionPersonIdList(session.present_ids));
    if (present.has(pid)) present.delete(pid);
    else present.add(pid);
    const presentIds = [...present];
    return {
      present_ids: presentIds,
      declined_ids: resolveDeclinedIdsAfterAttendanceConfirm(
        session,
        presentIds,
      ),
      present_despite_decline_ids: [
        ...new Set([
          ...normalizeSessionPersonIdList(session?.present_despite_decline_ids),
          ...resolvePresentDespiteDeclineIds(session, presentIds),
        ]),
      ],
    };
  }

  const declined = new Set(normalizeSessionPersonIdList(session.declined_ids));
  if (declined.has(pid)) declined.delete(pid);
  else declined.add(pid);
  return {
    declined_ids: [...declined].filter((id) => planned.includes(id)),
  };
}
